// Path: sec-parser/client/src/pages/UsersPage.jsx
import React, { useState, useEffect } from "react";
import {
  Card, Table, Button, Tag, Typography, Space, Modal,
  Input, Select, Switch, Popconfirm, Form, Alert, notification,
} from "antd";
import {
  UserAddOutlined, EditOutlined, DeleteOutlined,
  ReloadOutlined, TeamOutlined,
} from "@ant-design/icons";
import { getUsers, createUser, updateUser, deleteUser } from "../services/api";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

const { Title, Text } = Typography;

export default function UsersPage() {
  const currentUser = useSelector((s) => s.auth.user);
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [modal, setModal]       = useState(null); // null | "create" | { user }
  const [saving, setSaving]     = useState(false);
  const [form, setForm]         = useState({});
  const [error, setError]       = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await getUsers(); setUsers(data); }
    catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm({ name: "", email: "", password: "", role: "analyst" });
    setError(null);
    setModal("create");
  };

  const openEdit = (user) => {
    setForm({ name: user.name, role: user.role, isActive: user.isActive });
    setError(null);
    setModal(user);
  };

  const handleSave = async () => {
    setError(null);
    setSaving(true);
    try {
      if (modal === "create") {
        await createUser(form);
        notification.success({ message: `User ${form.email} created` });
      } else {
        await updateUser(modal._id, form);
        notification.success({ message: "User updated" });
      }
      setModal(null);
      load();
    } catch (err) {
      setError(err.response?.data?.error || "Save failed");
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    await deleteUser(id);
    notification.success({ message: "User deleted" });
    load();
  };

  const handleToggleActive = async (user) => {
    await updateUser(user._id, { isActive: !user.isActive });
    load();
  };

  return (
    <div style={{ maxWidth: 900, margin: "0 auto" }}>
      <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 16 }}>
        <div>
          <Title level={3} style={{ margin: 0, color: "#1A3C5E" }}>
            <TeamOutlined style={{ marginRight: 8 }} />User Management
          </Title>
          <Text type="secondary">Invite-only access — admin creates all accounts</Text>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load} />
          <Button
            type="primary"
            icon={<UserAddOutlined />}
            onClick={openCreate}
            style={{ background: "#1A3C5E" }}
          >
            Add User
          </Button>
        </Space>
      </Space>

      <Card>
        <Table
          dataSource={users}
          rowKey="_id"
          loading={loading}
          columns={[
            { title: "Name",  dataIndex: "name",  render: (v) => <Text strong>{v}</Text> },
            { title: "Email", dataIndex: "email", render: (v) => <Text copyable>{v}</Text> },
            {
              title: "Role",
              dataIndex: "role",
              render: (v) => <Tag color={v === "admin" ? "red" : "blue"}>{v.toUpperCase()}</Tag>,
            },
            {
              title: "Active",
              dataIndex: "isActive",
              render: (v, record) => (
                <Switch
                  checked={v}
                  size="small"
                  disabled={record._id === currentUser?._id}
                  onChange={() => handleToggleActive(record)}
                />
              ),
            },
            {
              title: "Last Login",
              dataIndex: "lastLoginAt",
              render: (v) => v ? dayjs(v).format("MMM D, YYYY h:mm A") : "Never",
            },
            {
              title: "Created",
              dataIndex: "createdAt",
              render: (v) => dayjs(v).format("MMM D, YYYY"),
            },
            {
              title: "Actions",
              key: "actions",
              render: (_, record) => (
                <Space>
                  <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>
                    Edit
                  </Button>
                  {record._id !== currentUser?._id && (
                    <Popconfirm
                      title="Delete this user? This cannot be undone."
                      onConfirm={() => handleDelete(record._id)}
                    >
                      <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  )}
                </Space>
              ),
            },
          ]}
        />
      </Card>

      {/* Create / Edit Modal */}
      <Modal
        open={!!modal}
        title={modal === "create" ? "Add New User" : `Edit: ${modal?.name}`}
        onOk={handleSave}
        onCancel={() => setModal(null)}
        confirmLoading={saving}
        okText={modal === "create" ? "Create User" : "Save Changes"}
        okButtonProps={{ style: { background: "#1A3C5E" } }}
      >
        <Space direction="vertical" size={12} style={{ width: "100%", marginTop: 8 }}>
          {error && <Alert type="error" message={error} showIcon />}

          <div>
            <Text strong>Full Name</Text>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Jane Smith"
              style={{ marginTop: 4 }}
            />
          </div>

          {modal === "create" && (
            <>
              <div>
                <Text strong>Email</Text>
                <Input
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="jane@company.com"
                  style={{ marginTop: 4 }}
                />
              </div>
              <div>
                <Text strong>Temporary Password</Text>
                <Input.Password
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Min. 8 characters"
                  style={{ marginTop: 4 }}
                />
                <Text type="secondary" style={{ fontSize: 11 }}>
                  User should change this after first login.
                </Text>
              </div>
            </>
          )}

          <div>
            <Text strong>Role</Text>
            <Select
              value={form.role}
              onChange={(v) => setForm({ ...form, role: v })}
              style={{ width: "100%", marginTop: 4 }}
              options={[
                { value: "analyst", label: "Analyst — can upload, benchmark, review" },
                { value: "admin",   label: "Admin — full access + user management" },
              ]}
            />
          </div>
        </Space>
      </Modal>
    </div>
  );
}